import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { httpsUrl } from '@/lib/safeUrl';

const PLAY_BADGE =
  'https://res.cloudinary.com/dvtqocatk/image/upload/v1783618337/GetItOnGooglePlay_Badge_Web_color_English_prmgb6.png';
const DISMISSED_KEY = 'recovry_app_prompt_dismissed_at';
const SNOOZE_DAYS = 14;

declare global {
  interface Window {
    __PRERENDER__?: boolean;
  }
}

/**
 * Android visitors get one offer to switch to the app. Once dismissed it stays
 * quiet for two weeks; it never shows inside the installed app, during
 * prerendering, or on any other platform, where there is nothing to install.
 */
export function AppInstallPrompt() {
  const { user_app_android_url } = useSiteConfig();
  const url = httpsUrl(user_app_android_url) ?? '';
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!url || window.__PRERENDER__) return;
    if (!/Android/i.test(navigator.userAgent)) return;
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    try {
      const dismissedAt = Number(localStorage.getItem(DISMISSED_KEY) || 0);
      if (Date.now() - dismissedAt < SNOOZE_DAYS * 86_400_000) return;
    } catch {
      /* storage blocked: still offer once this session */
    }
    const timer = window.setTimeout(() => setOpen(true), 1200);
    return () => window.clearTimeout(timer);
  }, [url]);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  if (!url) return null;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && dismiss()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Get the Recovry app</DialogTitle>
          <DialogDescription>
            Faster booking, appointment reminders and your joining links in one place.
          </DialogDescription>
        </DialogHeader>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={dismiss}
          className="mx-auto mt-2 block"
          aria-label="Get it on Google Play"
        >
          <img src={PLAY_BADGE} alt="Get it on Google Play" width={180} height={53} className="h-[53px] w-auto" />
        </a>
        <Button variant="ghost" size="sm" className="mx-auto mt-1 text-muted-foreground" onClick={dismiss}>
          Continue on web
        </Button>
      </DialogContent>
    </Dialog>
  );
}
